import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { crmApi, errMsg } from '@crm/api';

/**
 * Фабрика RTK-слайса для простого branch-scoped CRUD-ресурса.
 *
 * Возвращает `reducer`, `thunks` и набор селекторов. Состояние индексируется
 * по `branchId`, чтобы при переключении филиала не смешивались данные.
 *
 * @param {object} opts
 * @param {string} opts.name      — имя слайса и префикс action-типов
 * @param {string} opts.path      — часть URL после `/crm/:branchId` (например, 'managers')
 * @param {string} [opts.itemsKey='items']    — ключ массива в ответе списка
 * @param {string} opts.singleKey             — ключ одиночной сущности в ответе (напр. 'manager')
 * @param {'put'|'patch'} [opts.updateMethod='patch'] — HTTP-метод для update
 */
export function createCrudSlice({
  name,
  path,
  itemsKey = 'items',
  singleKey,
  updateMethod = 'patch',
}) {
  if (!singleKey) throw new Error(`createCrudSlice(${name}): singleKey is required`);

  const initialBranchState = () => ({
    list: [],
    total: 0,
    listStatus: 'idle',
    listError: null,
    saveStatus: 'idle',
    saveError: null,
    current: null,
  });

  const getBranchState = (state, branchId) => {
    if (!state.byBranch[branchId]) state.byBranch[branchId] = initialBranchState();
    return state.byBranch[branchId];
  };

  const list = createAsyncThunk(
    `${name}/list`,
    async ({ branchId, params = {} }, { rejectWithValue }) => {
      try {
        const { data } = await crmApi(branchId).get(`/${path}`, { params });
        return {
          branchId,
          items: data[itemsKey] ?? [],
          total: data.total ?? (data[itemsKey]?.length ?? 0),
        };
      } catch (err) {
        return rejectWithValue(errMsg(err, 'Не удалось загрузить список'));
      }
    },
  );

  const fetchOne = createAsyncThunk(
    `${name}/fetchOne`,
    async ({ branchId, id }, { rejectWithValue }) => {
      try {
        const { data } = await crmApi(branchId).get(`/${path}/${id}`);
        return { branchId, item: data[singleKey] ?? data };
      } catch (err) {
        return rejectWithValue(errMsg(err, 'Не удалось загрузить запись'));
      }
    },
  );

  const create = createAsyncThunk(
    `${name}/create`,
    async ({ branchId, payload }, { rejectWithValue }) => {
      try {
        const { data } = await crmApi(branchId).post(`/${path}`, payload);
        return { branchId, item: data[singleKey] ?? data };
      } catch (err) {
        return rejectWithValue(errMsg(err, 'Не удалось создать запись'));
      }
    },
  );

  const update = createAsyncThunk(
    `${name}/update`,
    async ({ branchId, id, payload }, { rejectWithValue }) => {
      try {
        const { data } = await crmApi(branchId)[updateMethod](`/${path}/${id}`, payload);
        return { branchId, item: data[singleKey] ?? data };
      } catch (err) {
        return rejectWithValue(errMsg(err, 'Не удалось обновить запись'));
      }
    },
  );

  const remove = createAsyncThunk(
    `${name}/remove`,
    async ({ branchId, id }, { rejectWithValue }) => {
      try {
        await crmApi(branchId).delete(`/${path}/${id}`);
        return { branchId, id };
      } catch (err) {
        return rejectWithValue(errMsg(err, 'Не удалось удалить запись'));
      }
    },
  );

  const slice = createSlice({
    name,
    initialState: { byBranch: {} },
    reducers: {
      clear: (state, action) => {
        const branchId = action.payload;
        if (branchId) delete state.byBranch[branchId];
      },
      setCurrent: (state, action) => {
        const { branchId, item } = action.payload;
        getBranchState(state, branchId).current = item;
      },
      resetSave: (state, action) => {
        const s = getBranchState(state, action.payload);
        s.saveStatus = 'idle';
        s.saveError = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(list.pending, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.listStatus = 'loading';
          s.listError = null;
        })
        .addCase(list.fulfilled, (state, action) => {
          const { branchId, items, total } = action.payload;
          const s = getBranchState(state, branchId);
          s.listStatus = 'idle';
          s.list = items;
          s.total = total;
        })
        .addCase(list.rejected, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.listStatus = 'error';
          s.listError = action.payload;
        })
        .addCase(fetchOne.fulfilled, (state, action) => {
          const { branchId, item } = action.payload;
          getBranchState(state, branchId).current = item;
        })
        .addCase(create.pending, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.saveStatus = 'loading';
          s.saveError = null;
        })
        .addCase(create.fulfilled, (state, action) => {
          const { branchId, item } = action.payload;
          const s = getBranchState(state, branchId);
          s.saveStatus = 'idle';
          s.list = [item, ...s.list];
          s.total += 1;
        })
        .addCase(create.rejected, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.saveStatus = 'error';
          s.saveError = action.payload;
        })
        .addCase(update.pending, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.saveStatus = 'loading';
          s.saveError = null;
        })
        .addCase(update.fulfilled, (state, action) => {
          const { branchId, item } = action.payload;
          const s = getBranchState(state, branchId);
          s.saveStatus = 'idle';
          s.list = s.list.map((it) => (it.id === item.id ? { ...it, ...item } : it));
          if (s.current?.id === item.id) s.current = { ...s.current, ...item };
        })
        .addCase(update.rejected, (state, action) => {
          const s = getBranchState(state, action.meta.arg.branchId);
          s.saveStatus = 'error';
          s.saveError = action.payload;
        })
        .addCase(remove.fulfilled, (state, action) => {
          const { branchId, id } = action.payload;
          const s = getBranchState(state, branchId);
          s.list = s.list.filter((it) => it.id !== id);
          s.total = Math.max(0, s.total - 1);
          if (s.current?.id === id) s.current = null;
        });
    },
  });

  const pickBranch = (state) => state[name].byBranch;
  const selectList = (branchId) => (state) => pickBranch(state)[branchId]?.list ?? [];
  const selectTotal = (branchId) => (state) => pickBranch(state)[branchId]?.total ?? 0;
  const selectListStatus = (branchId) => (state) =>
    pickBranch(state)[branchId]?.listStatus ?? 'idle';
  const selectListError = (branchId) => (state) => pickBranch(state)[branchId]?.listError ?? null;
  const selectSaveStatus = (branchId) => (state) =>
    pickBranch(state)[branchId]?.saveStatus ?? 'idle';
  const selectSaveError = (branchId) => (state) => pickBranch(state)[branchId]?.saveError ?? null;
  const selectCurrent = (branchId) => (state) => pickBranch(state)[branchId]?.current ?? null;
  const selectById = (branchId, id) => (state) =>
    (pickBranch(state)[branchId]?.list ?? []).find((it) => it.id === id) ?? null;

  return {
    slice,
    reducer: slice.reducer,
    actions: slice.actions,
    thunks: { list, fetchOne, create, update, remove },
    selectors: {
      selectList,
      selectTotal,
      selectListStatus,
      selectListError,
      selectSaveStatus,
      selectSaveError,
      selectCurrent,
      selectById,
    },
  };
}
